/* import __COLOCATED_TEMPLATE__ from './operator-workflow-event.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type OperatorWorkflowEvent from 'embercom/objects/inbox/renderable/operator-workflow-event';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import LinkWithText from 'embercom/helpers/link-with-text';
import { humanReadableObjectNamesKeys } from 'embercom/models/data/matching-system/matching-constants';
import simpleCapitalize from 'embercom/lib/simple-capitalize';
import type Conversation from 'embercom/objects/inbox/conversation';
import { createSafeAdminLink } from 'embercom/utils/admin-link';
import { buildStepReasoningEvent } from '../helpers/fin-thoughts-event';
import { truncateEventText, isEventTextTruncated } from 'embercom/lib/inbox2/event-text-truncation';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import type { OperatorWorkflowAiAgentEvent } from './operator-workflow-ai-agent-event';

interface OperatorWorkflowEventPart extends RenderablePart {
  renderableData: OperatorWorkflowEvent;
}

interface Signature {
  Args: {
    part: OperatorWorkflowEventPart;
    conversation: Conversation;
  };
}

export default class Inbox2ConversationStreamEventPartsOperatorWorkflowEventComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;

  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get operatorName() {
    let admin = this.aiAgentEventData.admin;
    if (admin) {
      return admin.name;
    } else {
      // fallback to default operator name Fin
      return this.intl.t('inbox.operator');
    }
  }

  get adminTitle() {
    let admin = this.args.part.renderableData.eventData.admin;

    if (admin) {
      return createSafeAdminLink(admin, this.linkWithTextHelper);
    } else {
      return '';
    }
  }

  get skipAssignmentActionData() {
    let skipAssignmentAction = this.args.part.renderableData.eventData.skipAssignmentAction;

    if (skipAssignmentAction) {
      let outboundType = this.intl.t(
        humanReadableObjectNamesKeys[skipAssignmentAction.outboundType],
        {
          count: 1,
        },
      );
      let link = this.linkWithTextHelper.compute([
        `apps.app.outbound.find`,
        skipAssignmentAction.outboundId,
        skipAssignmentAction.outboundName,
      ]);
      return {
        outboundType: simpleCapitalize(outboundType),
        outboundName: link,
      };
    } else {
      return {
        outboundType: '',
        outboundName: '',
      };
    }
  }

  get aiAgentEventData() {
    return this.args.part.renderableData.eventData;
  }

  get isHiddenEvent(): boolean {
    return this.args.part.renderableData.eventType === 'procedure_handover';
  }

  get aiAgentEvent(): OperatorWorkflowAiAgentEvent | null {
    switch (this.args.part.renderableData.eventType) {
      case 'ai_agent_workflow_started': {
        let hasReasoning = Boolean(this.aiAgentEventData?.aiAgentWorkflowStarted?.reasoning);
        return {
          type: hasReasoning ? 'workflow_reasoning' : 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_workflow_started',
          operatorName: this.operatorName,
          id: this.aiAgentEventData?.aiAgentWorkflowStarted?.id,
          rulesetId: this.aiAgentEventData?.aiAgentWorkflowStarted?.rulesetId,
          title:
            this.aiAgentEventData?.aiAgentWorkflowStarted?.title ||
            this.intl.t('inbox.operator-workflow-event.events.workflow'),
          iconName: 'custom-attribute',
          reason: this.aiAgentEventData?.aiAgentWorkflowStarted?.reasoning,
        };
      }
      case 'ai_agent_workflow_finished':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_workflow_finished',
          operatorName: this.operatorName,
          id: this.aiAgentEventData?.aiAgentWorkflowFinished?.id,
          rulesetId: this.aiAgentEventData?.aiAgentWorkflowFinished?.rulesetId,
          title:
            this.aiAgentEventData?.aiAgentWorkflowFinished?.title ||
            this.intl.t('inbox.operator-workflow-event.events.workflow'),
          iconName: 'custom-attribute',
        };
      case 'ai_agent_workflow_stopped':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_workflow_stopped',
          operatorName: this.operatorName,
          id: this.aiAgentEventData?.aiAgentWorkflowStopped?.id,
          rulesetId: this.aiAgentEventData?.aiAgentWorkflowStopped?.rulesetId,
          title:
            this.aiAgentEventData?.aiAgentWorkflowStopped?.title ||
            this.intl.t('inbox.operator-workflow-event.events.workflow'),
          iconName: 'custom-attribute',
        };
      case 'ai_agent_workflow_failed':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_workflow_failed',
          operatorName: this.operatorName,
          id: this.aiAgentEventData?.aiAgentWorkflowFailed?.id,
          rulesetId: this.aiAgentEventData?.aiAgentWorkflowFailed?.rulesetId,
          title:
            this.aiAgentEventData?.aiAgentWorkflowFailed?.title ||
            this.intl.t('inbox.operator-workflow-event.events.workflow'),
          iconName: 'custom-attribute',
        };
      case 'ai_agent_task_started':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_started',
          operatorName: this.operatorName,
          title:
            this.aiAgentEventData?.aiAgentTaskStarted?.title ||
            this.intl.t('inbox.operator-workflow-event.events.task'),
          iconName: 'fin',
        };
      case 'ai_agent_task_finished':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_finished',
          operatorName: this.operatorName,
          title:
            this.aiAgentEventData?.aiAgentTaskFinished?.title ||
            this.intl.t('inbox.operator-workflow-event.events.task'),
          iconName: 'fin',
        };
      case 'ai_agent_task_stopped':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_stopped',
          operatorName: this.operatorName,
          title:
            this.aiAgentEventData?.aiAgentTaskStopped?.title ||
            this.intl.t('inbox.operator-workflow-event.events.task'),
          iconName: 'custom-attribute',
        };
      case 'ai_agent_task_failed':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_failed',
          operatorName: this.operatorName,
          title:
            this.aiAgentEventData?.aiAgentTaskFailed?.title ||
            this.intl.t('inbox.operator-workflow-event.events.task'),
          reason: this.aiAgentEventData?.aiAgentTaskFailed?.reason,
          iconName: 'fin',
        };
      case 'ai_agent_task_paused':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_paused',
          operatorName: this.operatorName,
          title:
            this.aiAgentEventData?.aiAgentTaskPaused?.title ||
            this.intl.t('inbox.operator-workflow-event.events.task'),
          iconName: 'fin',
        };
      case 'ai_agent_set_attributes': {
        let attributes = this.aiAgentEventData?.aiAgentSetAttributes;
        if (!attributes) {
          return null;
        }
        return {
          type: 'set_attributes',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_set_attributes.set',
          operatorName: this.operatorName,
          attributeName: attributes[0]?.name,
          attributeValue: attributes[0]?.value,
          remainingAttributesCount: attributes.length ? attributes.length - 1 : 0,
          attributes,
          iconName: 'insert-data',
        };
      }
      case 'ai_agent_task_step_reasoning': {
        let stepReasoning = this.aiAgentEventData?.aiAgentTaskStepReasoning;
        return buildStepReasoningEvent(this.operatorName, stepReasoning);
      }
      case 'ai_agent_conditional_evaluation': {
        let rawStepNumber = this.aiAgentEventData?.aiAgentConditionalEvaluation?.step_number;
        let skipped = this.aiAgentEventData?.aiAgentConditionalEvaluation?.skipped_condition;
        // match everything before the last dot and everything after the last dot
        let match = rawStepNumber?.match(/^(.+)\.(\d+)$/);
        let subProcedure = match ? match[1] : null;
        // answerbot uses main_section for the main procedure in the step number for procedures
        if (subProcedure === `main_section`) {
          subProcedure = null;
        }
        let stepNumber = match ? match[2] : rawStepNumber;

        let i18nKey;
        if (skipped && subProcedure) {
          i18nKey = 'inbox.operator-workflow-event.events.ai_agent_conditional_evaluation_skipped';
        } else if (skipped && !subProcedure) {
          i18nKey =
            'inbox.operator-workflow-event.events.ai_agent_conditional_evaluation_skipped_main_procedure';
        } else if (!skipped && subProcedure) {
          i18nKey = 'inbox.operator-workflow-event.events.ai_agent_conditional_evaluation';
        } else {
          i18nKey =
            'inbox.operator-workflow-event.events.ai_agent_conditional_evaluation_main_procedure';
        }

        return {
          i18nKey,
          operatorName: this.operatorName,
          selected_branch_number: String(
            this.aiAgentEventData?.aiAgentConditionalEvaluation?.selected_branch_number,
          ),
          step_number: stepNumber,
          skipped,
          sub_procedure: subProcedure || undefined,
          iconName: 'branch',
          type: 'conditional_evaluation',
        };
      }
      case 'ai_agent_content_retrieval': {
        let contentRetrieval = this.aiAgentEventData?.aiAgentContentRetrieval;
        if (!contentRetrieval) {
          return null;
        }
        return {
          type: 'content_retrieval',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_content_retrieval',
          operatorName: this.operatorName,
          iconName: 'article',
          query: contentRetrieval.query,
          document_count: contentRetrieval.document_count,
          passage_count: contentRetrieval.passage_count,
          retrieved_content: contentRetrieval.retrieved_content,
          step_number: contentRetrieval.step_number,
        };
      }
      case 'ai_agent_skipped_steps': {
        let skippedSteps = this.aiAgentEventData?.aiAgentSkippedSteps?.skipped_steps;
        if (!skippedSteps) {
          return null;
        }
        let i18nKey = 'inbox.operator-workflow-event.events.ai_agent_skipped_step';
        if (skippedSteps.length > 1) {
          i18nKey = 'inbox.operator-workflow-event.events.ai_agent_skipped_step_plural';
        }
        let stepNumber = '';
        let step_numbers = skippedSteps.map(
          (step: { section_name: string; step_number: string }) => {
            if (step.section_name === 'Main Section') {
              return step.step_number;
            } else {
              return `${step.section_name} ${step.step_number}`;
            }
          },
        );
        if (step_numbers.length > 2) {
          stepNumber = `${step_numbers.slice(0, -1).join(', ')} & ${step_numbers.slice(-1)}`;
        } else {
          stepNumber = step_numbers.join(' & ');
        }
        return {
          type: 'skipped_steps',
          i18nKey,
          operatorName: this.operatorName,
          step_number: stepNumber,
          iconName: null,
        };
      }
      case 'ai_agent_code_condition_error': {
        let codeConditionError = this.aiAgentEventData?.aiAgentCodeConditionError;
        if (!codeConditionError) {
          return null;
        }
        return {
          type: 'code_condition_error',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_code_condition_error.title',
          operatorName: this.operatorName,
          step_number: codeConditionError.step_number,
          condition: codeConditionError.condition,
          error_message: codeConditionError.error_message,
          iconName: 'alert',
        };
      }
      case 'email_verification_successful':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.email_verification_successful',
          operatorName: this.operatorName,
          iconName: 'locked',
          type: 'default',
        };
      case 'email_verification_failed':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.email_verification_failed',
          operatorName: this.operatorName,
          iconName: 'locked',
          type: 'default',
        };
      case 'email_already_verified':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.email_already_verified',
          operatorName: this.operatorName,
          iconName: 'locked',
          type: 'default',
        };
      case 'email_triggered_for_verification':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.email_triggered_for_verification',
          operatorName: this.operatorName,
          iconName: 'webhook',
          type: 'default',
          title:
            this.aiAgentEventData?.emailTriggeredForVerification?.title ||
            this.intl.t('inbox.operator-workflow-event.events.action'),
          actionId: this.aiAgentEventData?.emailTriggeredForVerification?.actionId,
        };
      case 'phone_verification_successful':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.phone_verification_successful',
          operatorName: this.operatorName,
          iconName: 'locked',
          type: 'default',
        };
      case 'phone_verification_failed':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.phone_verification_failed',
          operatorName: this.operatorName,
          iconName: 'locked',
          type: 'default',
        };
      case 'impersonated_user_data_update_notice':
        return {
          i18nKey: 'inbox.operator-workflow-event.events.impersonated_user_data_update_notice',
          operatorName: this.operatorName,
          iconName: null,
          type: 'default',
          userDisplayName: this.aiAgentEventData.impersonatedUserDataUpdateNotice?.userDisplayName,
        };
      case 'ai_agent_goto_section':
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_goto_section',
          operatorName: this.operatorName,
          title:
            this.aiAgentEventData?.aiAgentGotoSection?.sectionName ||
            this.intl.t('inbox.operator-workflow-event.events.sub_procedure'),
          iconName: 'branch',
        };
      case 'ai_agent_end_section': {
        let sectionName =
          this.aiAgentEventData?.aiAgentEndSection?.sectionName ||
          this.intl.t('inbox.operator-workflow-event.events.sub_procedure');
        let returnedTo =
          this.aiAgentEventData?.aiAgentEndSection?.returnedToSectionName ||
          this.intl.t('inbox.operator-workflow-event.events.main_procedure');
        return {
          type: 'default',
          i18nKey: 'inbox.operator-workflow-event.events.ai_agent_end_section',
          operatorName: this.operatorName,
          title: this.intl.t('inbox.operator-workflow-event.events.ai_agent_end_section_title', {
            sectionName,
            returnedTo,
            htmlSafe: true,
          }),
          iconName: 'branch',
        };
      }
      default:
        return null;
    }
  }

  get translatedEvent() {
    switch (this.args.part.renderableData.eventType) {
      case 'wait_started':
        return this.waitStartedEvent;
      case 'start_workflow_requested':
        return this.intl.t('inbox.operator-workflow-event.events.start_workflow_requested', {
          admin: this.adminTitle,
          htmlSafe: true,
        });
      case 'skip_assignment_action':
        return this.intl.t('inbox.operator-workflow-event.events.skip_assignment_action', {
          ...this.skipAssignmentActionData,
          htmlSafe: true,
        });
      case 'workflow_finished':
        return this.intl.t(
          `inbox.operator-workflow-event.events.workflow_finished.${
            this.args.part.renderableData.eventData.workflowFinished!.reason
          }`,
          {
            htmlSafe: true,
          },
        );
      case 'tag_user':
        return this.intl.t('inbox.operator-workflow-event.events.tag_user', {
          tag: this.tagName,
          htmlSafe: true,
        });
      case 'remove_tag_from_user':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_user', {
          tag: this.tagName,
          htmlSafe: true,
        });
      case 'add_tag_to_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.add_tag_to_conversation', {
          tag: this.tagName,
          htmlSafe: true,
        });
      case 'remove_tag_from_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_conversation', {
          tag: this.tagName,
          htmlSafe: true,
        });
      case 'user_inactivity_conversation_autoclosed':
        return this.intl.t(
          'inbox.operator-workflow-event.events.user_inactivity_conversation_autoclosed',
          {
            minutes:
              this.args.part.renderableData.eventData.userInactivityConversationAutoClosed!
                .endUserInactivityTimer / 60,
            htmlSafe: true,
          },
        );
      case 'language_override_updated':
        if (!this.args.part.renderableData.eventData.languageOverrideUpdated!.previousValue) {
          return this.intl.t('inbox.operator-workflow-event.events.language_override_updated.set', {
            language: this.intl.languageNameFromCode(
              this.args.part.renderableData.eventData.languageOverrideUpdated!.newValue,
            ),
            htmlSafe: true,
          });
        }
        return this.intl.t(
          'inbox.operator-workflow-event.events.language_override_updated.changed',
          {
            previousValue: this.intl.languageNameFromCode(
              this.args.part.renderableData.eventData.languageOverrideUpdated!.previousValue,
            ),
            newValue: this.intl.languageNameFromCode(
              this.args.part.renderableData.eventData.languageOverrideUpdated!.newValue,
            ),
            htmlSafe: true,
          },
        );
      case 'user_data_attribute_updated':
        return this.userDataAttributeUpdatedEvent;
      case 'ai_agent_ignoring_user_message':
        return this.intl.t(
          `inbox.operator-workflow-event.events.ai_agent_ignoring_user_message.${
            this.args.part.renderableData.eventData.aiAgentIgnoringUserMessage!.reason
          }`,
          {
            htmlSafe: true,
          },
        );
      default:
        return this.intl.t(
          `inbox.operator-workflow-event.events.${this.args.part.renderableData.eventType}`,
          {
            default: 'inbox.operator-workflow-event.events.unknown',
            ...this.args.part.renderableData.eventData,
          },
        );
    }
  }

  get waitStartedEvent() {
    let durationType =
      this.args.part.renderableData.eventData.waitStarted?.durationType || 'custom';

    if (durationType === 'custom') {
      let customDuration = this.args.part.renderableData.eventData.waitStarted?.customDuration || 0;
      let days = Math.floor(customDuration / 86400);
      let hours = Math.floor((customDuration % 86400) / 3600);
      let minutes = Math.floor((customDuration % 3600) / 60);
      let seconds = Math.floor(customDuration % 60);

      let formatKey = '';
      if (days > 0) {
        formatKey += 'd';
      }
      if (hours > 0) {
        formatKey += 'h';
      }
      if (minutes > 0) {
        formatKey += 'm';
      }
      if (seconds > 0) {
        formatKey += 's';
      }

      return this.intl.t(`inbox.operator-workflow-event.events.wait_started.${formatKey}`, {
        days,
        hours,
        minutes,
        seconds,
      });
    } else {
      return this.intl.t(`inbox.operator-workflow-event.events.wait_started.${durationType}`);
    }
  }

  get tagName() {
    let actionData = this.args.part.renderableData.eventData.actionData;

    if (!actionData) {
      return '';
    }

    if (actionData.tagId) {
      return this.session.store.peekRecord('tag', actionData.tagId)?.name || '';
    }

    return '';
  }

  get truncatedTagName(): string {
    return truncateEventText(this.tagName);
  }

  get isTagNameTruncated(): boolean {
    return isEventTextTruncated(this.tagName);
  }

  get isTagEvent(): boolean {
    let eventType = this.args.part.renderableData.eventType;
    return (
      eventType === 'tag_user' ||
      eventType === 'remove_tag_from_user' ||
      eventType === 'add_tag_to_conversation' ||
      eventType === 'remove_tag_from_conversation'
    );
  }

  /**
   * Returns the translated event with the tag name truncated (no tooltip wrapper).
   * Used when we wrap the entire element in an IcTooltip.
   */
  get truncatedTranslatedEventWithoutTooltip(): string {
    let eventType = this.args.part.renderableData.eventType;
    let truncatedTag = truncateEventText(this.tagName);
    switch (eventType) {
      case 'tag_user':
        return this.intl.t('inbox.operator-workflow-event.events.tag_user', {
          tag: truncatedTag,
          htmlSafe: true,
        });
      case 'remove_tag_from_user':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_user', {
          tag: truncatedTag,
          htmlSafe: true,
        });
      case 'add_tag_to_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.add_tag_to_conversation', {
          tag: truncatedTag,
          htmlSafe: true,
        });
      case 'remove_tag_from_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_conversation', {
          tag: truncatedTag,
          htmlSafe: true,
        });
      default:
        return '';
    }
  }

  get truncatedTranslatedEvent(): string {
    // Note: This was previously used for inline title tooltips, but those are stripped by sanitization.
    // Keeping this for potential future use, but the template now uses truncatedTranslatedEventWithoutTooltip
    // combined with IcTooltip instead.
    return this.truncatedTranslatedEventWithoutTooltip;
  }

  /**
   * Returns the workflow title for tag events (from customBotSummary)
   */
  get workflowTitle(): string {
    return this.args.part.renderableData.customBotSummary?.title ?? '';
  }

  /**
   * Returns the workflow title as a hyperlink to the workflow
   */
  get workflowTitleLink(): string {
    let customBotSummary = this.args.part.renderableData.customBotSummary;
    if (!customBotSummary) {
      return '';
    }
    return this.linkWithTextHelper.compute([
      'apps.app.operator.custom-bots.custom-bot.show',
      customBotSummary.id,
      customBotSummary.title,
    ]);
  }

  /**
   * Returns the truncated workflow title as a hyperlink to the workflow
   */
  get truncatedWorkflowTitleLink(): string {
    let customBotSummary = this.args.part.renderableData.customBotSummary;
    if (!customBotSummary) {
      return '';
    }
    return this.linkWithTextHelper.compute([
      'apps.app.operator.custom-bots.custom-bot.show',
      customBotSummary.id,
      truncateEventText(customBotSummary.title),
    ]);
  }

  /**
   * Helper to get the v2 formatted tag event translation.
   * @param title - The workflow title link (may be truncated or full)
   * @param tag - The tag name (may be truncated or full)
   */
  private getTagEventV2Translation(title: string, tag: string): string {
    let eventType = this.args.part.renderableData.eventType;
    switch (eventType) {
      case 'tag_user':
        return this.intl.t('inbox.operator-workflow-event.events.tag_user-v2', {
          title,
          tag,
          htmlSafe: true,
        });
      case 'remove_tag_from_user':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_user-v2', {
          title,
          tag,
          htmlSafe: true,
        });
      case 'add_tag_to_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.add_tag_to_conversation-v2', {
          title,
          tag,
          htmlSafe: true,
        });
      case 'remove_tag_from_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_conversation-v2', {
          title,
          tag,
          htmlSafe: true,
        });
      default:
        return this.intl.t('inbox.operator-workflow-event.events.unknown-tag-event', {
          title,
          tag,
          htmlSafe: true,
        });
    }
  }

  /**
   * Returns the v2 formatted tag event with workflow title as a hyperlink.
   * Format: "Workflow: {title} added "{tag}"" where {title} is a link
   */
  get translatedTagEventV2(): string {
    return this.getTagEventV2Translation(this.workflowTitleLink, this.tagName);
  }

  /**
   * Returns the v2 formatted tag event with truncated workflow title as a hyperlink.
   */
  get truncatedTranslatedTagEventV2(): string {
    return this.getTagEventV2Translation(
      this.truncatedWorkflowTitleLink,
      truncateEventText(this.tagName),
    );
  }

  get isWorkflowTitleTruncated(): boolean {
    return isEventTextTruncated(this.workflowTitle);
  }

  /**
   * Returns the workflow part of the v2 tag event (e.g., "Workflow: {title}").
   * Used when both title and tag are truncated so each can have its own IcTooltip.
   */
  get tagEventV2WorkflowPart(): string {
    return this.intl.t('inbox.operator-workflow-event.events.tag_event_v2_workflow_part', {
      title: this.truncatedWorkflowTitleLink,
      htmlSafe: true,
    });
  }

  /**
   * Returns the tag part of the v2 tag event (e.g., 'added "{tag}"').
   * Used when both title and tag are truncated so each can have its own IcTooltip.
   */
  get tagEventV2TagPart(): string {
    let eventType = this.args.part.renderableData.eventType;
    let truncatedTag = truncateEventText(this.tagName);
    switch (eventType) {
      case 'tag_user':
        return this.intl.t('inbox.operator-workflow-event.events.tag_user_v2_tag_part', {
          tag: truncatedTag,
          htmlSafe: true,
        });
      case 'remove_tag_from_user':
        return this.intl.t(
          'inbox.operator-workflow-event.events.remove_tag_from_user_v2_tag_part',
          {
            tag: truncatedTag,
            htmlSafe: true,
          },
        );
      case 'add_tag_to_conversation':
        return this.intl.t(
          'inbox.operator-workflow-event.events.add_tag_to_conversation_v2_tag_part',
          {
            tag: truncatedTag,
            htmlSafe: true,
          },
        );
      case 'remove_tag_from_conversation':
        return this.intl.t(
          'inbox.operator-workflow-event.events.remove_tag_from_conversation_v2_tag_part',
          {
            tag: truncatedTag,
            htmlSafe: true,
          },
        );
      default:
        return this.intl.t('inbox.operator-workflow-event.events.unknown_tag_event_v2_tag_part', {
          tag: truncatedTag,
          htmlSafe: true,
        });
    }
  }

  /**
   * Returns the prefix part of the tag event translation (e.g., "Tag ").
   * Used when both title and tag are truncated so each can have its own IcTooltip.
   */
  get truncatedTagEventPrefix(): string {
    let eventType = this.args.part.renderableData.eventType;
    switch (eventType) {
      case 'tag_user':
        return this.intl.t('inbox.operator-workflow-event.events.tag_user_prefix');
      case 'remove_tag_from_user':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_user_prefix');
      case 'add_tag_to_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.add_tag_to_conversation_prefix');
      case 'remove_tag_from_conversation':
        return this.intl.t(
          'inbox.operator-workflow-event.events.remove_tag_from_conversation_prefix',
        );
      default:
        return '';
    }
  }

  /**
   * Returns the suffix part of the tag event translation (e.g., " has been added to the conversation").
   * Used when both title and tag are truncated so each can have its own IcTooltip.
   */
  get truncatedTagEventSuffix(): string {
    let eventType = this.args.part.renderableData.eventType;
    switch (eventType) {
      case 'tag_user':
        return this.intl.t('inbox.operator-workflow-event.events.tag_user_suffix');
      case 'remove_tag_from_user':
        return this.intl.t('inbox.operator-workflow-event.events.remove_tag_from_user_suffix');
      case 'add_tag_to_conversation':
        return this.intl.t('inbox.operator-workflow-event.events.add_tag_to_conversation_suffix');
      case 'remove_tag_from_conversation':
        return this.intl.t(
          'inbox.operator-workflow-event.events.remove_tag_from_conversation_suffix',
        );
      default:
        return '';
    }
  }

  truncateTitle(title: string | undefined): string {
    return truncateEventText(title);
  }

  isTitleTruncated(title: string | undefined): boolean {
    return isEventTextTruncated(title);
  }

  /**
   * Returns a link to the custom bot with the title truncated and wrapped in a span with title tooltip
   * Used when both title and tag are truncated to avoid IcTooltip conflicts
   * Returns a SafeString so the HTML doesn't get escaped when interpolated in translations
   */
  get truncatedCustomBotTitleWithTooltip(): ReturnType<typeof sanitizeHtml> {
    let customBotSummary = this.args.part.renderableData.customBotSummary;
    if (!customBotSummary) {
      return sanitizeHtml('');
    }
    let truncatedTitle = truncateEventText(customBotSummary.title);
    let link = this.linkWithTextHelper.compute([
      'apps.app.operator.custom-bots.custom-bot.show',
      customBotSummary.id,
      truncatedTitle,
    ]);
    // Wrap the link in a span with title tooltip (escape the title for the HTML attribute)
    return sanitizeHtml(
      `<span title="${customBotSummary.title}" class="cursor-default" data-test-truncated-title>${link}</span>`,
    );
  }

  get userDataAttributeUpdatedEvent() {
    let attribute = this.args.part.renderableData.eventData.actionData!.attributeIdentifier;
    let value = this.args.part.renderableData.eventData.actionData!.value?.toString();
    if (!attribute || !value) {
      return '';
    }

    if (attribute.startsWith('custom_data.')) {
      attribute = attribute.replace('custom_data.', '');
    }

    return this.intl.t('inbox.operator-workflow-event.events.user_data_attribute_updated', {
      attribute,
      value,
      htmlSafe: true,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::OperatorWorkflowEvent': typeof Inbox2ConversationStreamEventPartsOperatorWorkflowEventComponent;
    'inbox2/conversation-stream/event-parts/operator-workflow-event': typeof Inbox2ConversationStreamEventPartsOperatorWorkflowEventComponent;
  }
}
